import { useState, useEffect, useMemo, useRef } from 'react'
import { router } from '@inertiajs/react'
import { X, Trash2, Plus, GripVertical, Star, Copy } from 'lucide-react'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FILLED_REQUIRES_OWNER, LABELS, STATUS_LABELS, indefiniteArticle } from './labels'
import type { SeatDetail, SeatStatus, UserPickerEntry, SeatTag, Responsibility } from './types'
import { sortPeople } from '../../utils/peopleSort'
import { createChartMutationQueue, inertiaErrorMessage } from './chartStatePersistence'
import type { ChartMutation, ChartMutationQueue } from './chartStatePersistence'
import type { ChartFieldEdit } from './chartHistory'
import { NavigatorAvatarMarker, NavigatorBadge } from './NavigatorIndicator'

interface Props {
  chartId: number
  seat: SeatDetail
  canEdit: boolean
  pickerTeam: UserPickerEntry[]
  pickerOrg: UserPickerEntry[]
  orgTags: SeatTag[]
  onClose: () => void
  onDuplicate: (seat: SeatDetail) => void
  onDelete: (seat: SeatDetail) => void
  showFillToast: (seat: SeatDetail) => void
  // Pure view state from the chart toolbar toggle.
  showNavigatorStatus?: boolean
  onEditError?: (errors: unknown, fallback?: string) => void
  /**
   * A typed edit the SERVER has accepted, reported so the canvas can offer to
   * take it back. It is called once per settled edit — never per keystroke —
   * and only when the value actually changed.
   */
  onFieldEdit?: (edit: ChartFieldEdit) => void
  mutationQueue?: ChartMutationQueue
}

type ReportError = (errors: unknown, fallback?: string) => void
type EnqueueMutation = (
  item: Omit<ChartMutation, 'onError' | 'onCancel'>,
  fallback: string,
  onRejected?: () => void,
) => boolean

export default function SeatDetailPanel(props: Props) {
  const { chartId, seat, canEdit, pickerTeam, pickerOrg, orgTags, onClose, onEditError } = props
  const basePath = `/accountability_charts/${chartId}/seats/${seat.id}`
  const mutationQueue = useMemo(() => props.mutationQueue || createChartMutationQueue({ router }), [props.mutationQueue])
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])
  useEffect(() => () => {
    if (!props.mutationQueue) mutationQueue.dispose()
  }, [mutationQueue, props.mutationQueue])

  const [name, setName] = useState(seat.name)
  const [description, setDescription] = useState(seat.description || '')
  const [status, setStatus] = useState<SeatStatus>(seat.seat_status)
  const [editError, setEditError] = useState<string | null>(null)
  const nameRef = useRef(seat.name)
  const descriptionRef = useRef(seat.description || '')
  const statusRef = useRef<SeatStatus>(seat.seat_status)
  // The last values the server is known to hold, so a rejected change can be
  // rolled back instead of leaving the panel showing something unsaved, and so
  // an edit can say what it changed FROM.
  const savedNameRef = useRef(seat.name)
  const savedDescriptionRef = useRef(seat.description || '')
  const savedStatusRef = useRef<SeatStatus>(seat.seat_status)
  const serverNameRef = useRef(seat.name)
  const serverDescriptionRef = useRef(seat.description || '')
  const serverStatusRef = useRef<SeatStatus>(seat.seat_status)
  /**
   * The header values the server has ACCEPTED, per role, so a saved edit knows
   * what it replaced. Kept per role rather than in the refs above because
   * those follow the role on screen: a save can settle after the panel has
   * moved on, and the entry it leaves behind still has to name what that role
   * held before it. Successive saves update this in the order the server takes
   * them, so a second rename records the first one's value, not the original.
   */
  const acceptedHeadersRef = useRef<Record<number, { name: string; description: string; seat_status: SeatStatus }>>({})
  const seatIdentityRef = useRef(seat.id)
  seatIdentityRef.current = seat.id

  useEffect(() => {
    seatIdentityRef.current = seat.id
    acceptedHeadersRef.current[seat.id] = {
      name: seat.name,
      description: seat.description || '',
      seat_status: seat.seat_status,
    }
    nameRef.current = seat.name
    descriptionRef.current = seat.description || ''
    statusRef.current = seat.seat_status
    savedNameRef.current = seat.name
    savedDescriptionRef.current = seat.description || ''
    savedStatusRef.current = seat.seat_status
    serverNameRef.current = seat.name
    serverDescriptionRef.current = seat.description || ''
    serverStatusRef.current = seat.seat_status
    setName(seat.name)
    setDescription(seat.description || '')
    setStatus(seat.seat_status)
    setEditError(null)
  }, [seat.id])

  // The server can change these on its own — removing the last owner drops a
  // Filled role back to Open, and undo on the canvas puts a typed field back to
  // what it was — so adopt a value the chart data brought back rather than
  // keeping the stale one the editor was initialised with. Only a value that
  // actually CHANGED on the server is adopted, so the props a save of one's own
  // brings back never overwrite what is being typed.
  useEffect(() => {
    if (serverNameRef.current === seat.name) return
    serverNameRef.current = seat.name
    nameRef.current = seat.name
    savedNameRef.current = seat.name
    const accepted = acceptedHeadersRef.current[seat.id]
    if (accepted) accepted.name = seat.name
    setName(seat.name)
  }, [seat.id, seat.name])

  useEffect(() => {
    const incoming = seat.description || ''
    if (serverDescriptionRef.current === incoming) return
    serverDescriptionRef.current = incoming
    descriptionRef.current = incoming
    savedDescriptionRef.current = incoming
    const accepted = acceptedHeadersRef.current[seat.id]
    if (accepted) accepted.description = incoming
    setDescription(incoming)
  }, [seat.id, seat.description])

  useEffect(() => {
    if (serverStatusRef.current === seat.seat_status) return
    serverStatusRef.current = seat.seat_status
    statusRef.current = seat.seat_status
    savedStatusRef.current = seat.seat_status
    const accepted = acceptedHeadersRef.current[seat.id]
    if (accepted) accepted.seat_status = seat.seat_status
    setStatus(seat.seat_status)
  }, [seat.id, seat.seat_status])

  const reportError = (errors: unknown, fallback = `Unable to update this ${LABELS.seat.toLowerCase()}.`) => {
    if (!mountedRef.current || seatIdentityRef.current !== seat.id) return
    setEditError(inertiaErrorMessage(errors, fallback))
    onEditError?.(errors, fallback)
  }

  const enqueueMutation = (
    item: Omit<ChartMutation, 'onError' | 'onCancel'>,
    fallback: string,
    onRejected?: () => void,
  ) => mutationQueue.enqueue({
    ...item,
    onError: errors => { onRejected?.(); reportError(errors, fallback) },
    onCancel: () => { onRejected?.(); reportError({ base: 'This change was interrupted by navigation.' }, fallback) },
  })

  const rollBackStatus = () => {
    if (seatIdentityRef.current !== seat.id) return
    statusRef.current = savedStatusRef.current
    setStatus(savedStatusRef.current)
  }

  const saveHeader = (overrides: Partial<{ name: string; description: string; seat_status: SeatStatus }> = {}) => {
    if (!canEdit) return
    const payload = {
      name: overrides.name ?? nameRef.current,
      description: overrides.description ?? descriptionRef.current,
      seat_status: overrides.seat_status ?? statusRef.current,
    }
    const editedSeatId = seat.id
    // What to fall back on if nothing is known to have been accepted for this
    // role yet. The value this save actually replaced is read when the server
    // answers, not now: another save of the same role may settle first.
    const opened = {
      name: savedNameRef.current,
      description: savedDescriptionRef.current,
      seat_status: savedStatusRef.current,
    }
    setEditError(null)
    enqueueMutation({
      key: `seat:${seat.id}:header`,
      url: basePath,
      method: 'patch',
      data: { seat: payload },
      replacePending: true,
      onSuccess: () => {
        // The server has taken the edit, so it can be taken back — whether or
        // not this panel is still open on the role it was made to. Only the
        // panel's own state is left alone once it has moved on.
        const before = acceptedHeadersRef.current[editedSeatId] ?? opened
        acceptedHeadersRef.current[editedSeatId] = { ...payload }
        recordHeaderEdits(editedSeatId, before, payload)
        if (seatIdentityRef.current !== seat.id) return
        savedNameRef.current = payload.name
        savedDescriptionRef.current = payload.description
        savedStatusRef.current = payload.seat_status
        // The props this save brings back carry the values it just wrote, so
        // they must not read as a change made elsewhere and overwrite what is
        // being typed next.
        serverNameRef.current = payload.name
        serverDescriptionRef.current = payload.description
        serverStatusRef.current = payload.seat_status
        setEditError(null)
      },
    }, `Unable to update this ${LABELS.seat.toLowerCase()}.`, rollBackStatus)
  }

  /**
   * Hands the canvas the field this save settled, once the server has taken
   * it. One save carries all three header fields, so only the ones that
   * actually moved off their saved value become something to take back — a
   * blur that changed nothing records nothing.
   */
  const recordHeaderEdits = (
    editedSeatId: number,
    before: { name: string; description: string; seat_status: SeatStatus },
    payload: { name: string; description: string; seat_status: SeatStatus },
  ) => {
    const report = props.onFieldEdit
    if (!report) return
    if (payload.name !== before.name) {
      report({
        label: `renaming "${before.name}" to "${payload.name}"`,
        target: { type: 'seat', seatId: editedSeatId, field: 'name' },
        before: before.name,
        after: payload.name,
      })
    }
    if (payload.description !== before.description) {
      report({
        label: `the description of "${payload.name}"`,
        target: { type: 'seat', seatId: editedSeatId, field: 'description' },
        before: before.description,
        after: payload.description,
      })
    }
    if (payload.seat_status !== before.seat_status) {
      report({
        label: `the status of "${payload.name}"`,
        target: { type: 'seat', seatId: editedSeatId, field: 'seat_status' },
        before: before.seat_status,
        after: payload.seat_status,
      })
    }
  }

  const onNameChange = (value: string) => { nameRef.current = value; setName(value) }
  const onDescriptionChange = (value: string) => { descriptionRef.current = value; setDescription(value) }
  const onStatusChange = (s: SeatStatus) => {
    // Nobody holds this role, so Filled would only be rejected by the server.
    // Say so here and leave the dropdown on the status that is actually saved.
    if (s === 'filled' && seat.assignments.length === 0) {
      setStatus(statusRef.current)
      setEditError(FILLED_REQUIRES_OWNER)
      onEditError?.({ seat_status: FILLED_REQUIRES_OWNER }, FILLED_REQUIRES_OWNER)
      return
    }
    statusRef.current = s
    setStatus(s)
    saveHeader({ seat_status: s })
  }

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-elevated z-40 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-stone">
        <h3 className="text-h4 font-semibold text-text-primary">{LABELS.seat} Details</h3>
        <button onClick={onClose} className="p-1 hover:bg-stone/40 rounded"><X size={18} /></button>
      </div>
      {editError && (
        <div role="alert" className="flex items-center justify-between gap-2 bg-pinnacle-red/10 border-b border-pinnacle-red/30 px-4 py-2 text-xs text-pinnacle-red">
          <span>{editError}</span>
          <button type="button" onClick={() => setEditError(null)} className="underline">Dismiss</button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* A viewer reads the role; they are not shown greyed-out form fields
            they could never use. */}
        {canEdit ? (
          <section>
            <input
              value={name}
              onChange={e => onNameChange(e.target.value)}
              onBlur={() => saveHeader()}
              className="w-full text-h4 font-semibold text-text-primary border-b border-transparent focus:border-charcoal outline-none py-1"
            />
            <div className="mt-2 flex items-center gap-2">
              <label className="text-xs uppercase text-text-secondary font-semibold">Status</label>
              <select
                value={status}
                onChange={e => onStatusChange(e.target.value as SeatStatus)}
                className="px-2 py-1 border border-stone rounded-button text-sm"
              >
                {Object.keys(STATUS_LABELS).map(k => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
              </select>
            </div>
            <textarea
              value={description}
              onChange={e => onDescriptionChange(e.target.value)}
              onBlur={() => saveHeader()}
              placeholder="#1 Goal for the Seat."
              rows={3}
              className="w-full mt-3 px-3 py-2 border border-stone rounded-button text-sm focus:outline-none focus:border-charcoal"
            />
          </section>
        ) : (
          <section>
            <h4 className="w-full text-h4 font-semibold text-text-primary py-1">{seat.name}</h4>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs uppercase text-text-secondary font-semibold">Status</span>
              <span className="text-sm text-text-primary">{STATUS_LABELS[seat.seat_status]}</span>
            </div>
            {seat.description ? (
              <p className="mt-3 text-sm text-text-primary whitespace-pre-wrap">{seat.description}</p>
            ) : (
              <p className="mt-3 text-sm text-text-muted italic">No description.</p>
            )}
          </section>
        )}

        <OwnersSection
          basePath={basePath}
          seat={seat}
          canEdit={canEdit}
          pickerTeam={pickerTeam}
          pickerOrg={pickerOrg}
          showFillToast={props.showFillToast}
          showNavigatorStatus={props.showNavigatorStatus || false}
           onError={reportError}
           enqueueMutation={enqueueMutation}
        />

        <ResponsibilitiesSection basePath={basePath} seat={seat} canEdit={canEdit} onError={reportError} onFieldEdit={props.onFieldEdit} enqueueMutation={enqueueMutation} />

        <TagsSection basePath={basePath} seat={seat} orgTags={orgTags} canEdit={canEdit} enqueueMutation={enqueueMutation} />

        <CommentsSection basePath={basePath} seat={seat} canEdit={canEdit} enqueueMutation={enqueueMutation} />
      </div>

      {canEdit && (
        <div className="border-t border-stone p-3 flex items-center gap-2 flex-wrap">
          {canEdit && <button onClick={() => props.onDuplicate(seat)} className="text-sm px-3 py-1.5 border border-stone rounded-button hover:bg-stone/30 inline-flex items-center gap-1"><Copy size={14} />Duplicate</button>}
          {canEdit && (
            <button onClick={() => props.onDelete(seat)} className="text-sm px-3 py-1.5 border border-pinnacle-red text-pinnacle-red rounded-button hover:bg-pinnacle-red/5 inline-flex items-center gap-1 ml-auto">
              <Trash2 size={14} />Delete
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function OwnersSection({ basePath, seat, canEdit, pickerTeam, pickerOrg, showFillToast, showNavigatorStatus, onError, enqueueMutation }: {
  basePath: string
  seat: SeatDetail
  canEdit: boolean
  pickerTeam: UserPickerEntry[]
  pickerOrg: UserPickerEntry[]
  showFillToast: (seat: SeatDetail) => void
  showNavigatorStatus: boolean
  onError: ReportError
  enqueueMutation: EnqueueMutation
}) {
  const [showPicker, setShowPicker] = useState(false)
  const [external, setExternal] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const currentSeatIdRef = useRef(seat.id)
  currentSeatIdRef.current = seat.id
  useEffect(() => {
    submittingRef.current = false
    setSubmitting(false)
  }, [seat.id])

  const addUser = (userId: number) => {
    if (!canEdit || submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    const wasOpenAndEmpty = seat.seat_status === 'open' && seat.assignments.length === 0
    enqueueMutation({
      key: `seat:${seat.id}:assignment:add-user:${userId}`,
      url: `${basePath}/seat_assignments`,
      method: 'post',
      data: { seat_assignment: { user_id: userId, is_primary: seat.assignments.length === 0 } },
      onSuccess: () => { if (currentSeatIdRef.current === seat.id) { setShowPicker(false); if (wasOpenAndEmpty) showFillToast(seat) } },
      onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, `Unable to add this ${LABELS.assignee.toLowerCase()}.`)
  }
  const addExternal = () => {
    if (!canEdit || !external.trim() || submittingRef.current) return
    const normalizedExternal = external.trim().toLowerCase()
    if (seat.assignments.some(assignment => !assignment.is_internal && assignment.display_name.trim().toLowerCase() === normalizedExternal)) {
      onError({ base: `This ${LABELS.assignee.toLowerCase()} is already assigned to this ${LABELS.seat.toLowerCase()}.` })
      return
    }
    submittingRef.current = true
    setSubmitting(true)
    const wasOpenAndEmpty = seat.seat_status === 'open' && seat.assignments.length === 0
    enqueueMutation({
      key: `seat:${seat.id}:assignment:add-external`,
      url: `${basePath}/seat_assignments`,
      method: 'post',
      data: { seat_assignment: { external_name: external, is_primary: seat.assignments.length === 0 } },
      onSuccess: () => { if (currentSeatIdRef.current === seat.id) { setExternal(''); setShowPicker(false); if (wasOpenAndEmpty) showFillToast(seat) } },
      onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, `Unable to add this ${LABELS.assignee.toLowerCase()}.`)
  }
  const remove = (assignId: number) => {
    if (!canEdit) return
    if (!confirm(`Remove this ${LABELS.assignee.toLowerCase()}?`)) return
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:assignment:remove:${assignId}`,
      url: `${basePath}/seat_assignments/${assignId}`,
      method: 'delete',
      onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, `Unable to remove this ${LABELS.assignee.toLowerCase()}.`)
  }
  const setPrimary = (assignId: number) => {
    if (!canEdit || submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:assignment:primary:${assignId}`,
      url: `${basePath}/seat_assignments/${assignId}`,
      method: 'patch',
      data: { seat_assignment: { is_primary: true } },
      onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, `Unable to change the primary ${LABELS.assignee.toLowerCase()}.`)
  }

  const assignedUserIds = new Set(seat.assignments.flatMap(a => a.user_id === null ? [] : [a.user_id]))

  return (
    <section>
      <h4 className="text-xs uppercase tracking-wider font-bold text-text-primary mb-2">{LABELS.assignees}</h4>
      <ul className="space-y-1.5">
        {seat.assignments.map(a => (
          <li key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-stone/20">
            <div className="relative shrink-0">
              <div className={`w-7 h-7 rounded-full text-[11px] font-bold text-white flex items-center justify-center ${a.is_internal ? 'bg-sea' : 'bg-terrain'}`}>{a.initials}</div>
              {showNavigatorStatus && <NavigatorAvatarMarker status={a.navigator_status} name={a.display_name} />}
            </div>
            <span className="text-sm flex-1 text-text-primary">{a.display_name}{!a.is_internal && <span className="ml-2 text-xs text-text-muted">(external)</span>}</span>
            {showNavigatorStatus && <NavigatorBadge status={a.navigator_status} />}
            {canEdit && (
              <>
                <button onClick={() => setPrimary(a.id)} title="Make primary"
                  disabled={submitting}
                  className={`p-1 rounded ${a.is_primary ? 'text-marigold' : 'text-text-muted hover:text-marigold'}`}>
                  <Star size={14} fill={a.is_primary ? 'currentColor' : 'none'} />
                </button>
                <button onClick={() => remove(a.id)} disabled={submitting} className="p-1 text-text-muted hover:text-pinnacle-red disabled:opacity-50"><Trash2 size={14} /></button>
              </>
            )}
          </li>
        ))}
        {seat.assignments.length === 0 && <li className="text-sm text-text-muted italic">No {LABELS.assignees.toLowerCase()} yet.</li>}
      </ul>
      {canEdit && (
        <div className="mt-2">
          {!showPicker ? (
             <button onClick={() => setShowPicker(true)} disabled={submitting} className="text-sm text-text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50"><Plus size={14} />Add {LABELS.assignee.toLowerCase()}</button>
          ) : (
            <div className="border border-stone rounded-button p-2 space-y-2">
              <div className="max-h-40 overflow-y-auto">
                {pickerTeam.length > 0 && <p className="text-[10px] uppercase font-bold text-text-secondary mb-1">Team members</p>}
                {sortPeople(pickerTeam).map(u => (
                   <button key={u.id} onClick={() => addUser(u.id)} disabled={submitting || assignedUserIds.has(u.id)} className="w-full text-left text-sm px-2 py-1 hover:bg-stone/30 rounded disabled:opacity-50 disabled:cursor-not-allowed">{u.name}</button>
                ))}
                {pickerOrg.length > 0 && <p className="text-[10px] uppercase font-bold text-text-secondary mt-2 mb-1">Other people in org</p>}
                {sortPeople(pickerOrg).map(u => (
                   <button key={u.id} onClick={() => addUser(u.id)} disabled={submitting || assignedUserIds.has(u.id)} className="w-full text-left text-sm px-2 py-1 hover:bg-stone/30 rounded disabled:opacity-50 disabled:cursor-not-allowed">{u.name}</button>
                ))}
              </div>
              <div className="border-t border-stone pt-2">
                 <input value={external} onChange={e => setExternal(e.target.value)} disabled={submitting} placeholder="External person name…"
                  className="w-full px-2 py-1 border border-stone rounded-button text-sm" />
                <div className="flex justify-end gap-1 mt-1">
                   <button onClick={() => { setShowPicker(false); setExternal('') }} disabled={submitting} className="text-xs px-2 py-1 text-text-secondary disabled:opacity-50">Cancel</button>
                   <button onClick={addExternal} disabled={!external.trim() || submitting} className="text-xs px-2 py-1 bg-pinnacle-red text-white rounded disabled:opacity-50">Add external</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function SortableResponsibility({ r, seatId, canEdit, basePath, onDelete, onFieldEdit, enqueueMutation }: { r: Responsibility; seatId: number; canEdit: boolean; basePath: string; onDelete: (id: number) => void; onFieldEdit?: (edit: ChartFieldEdit) => void; enqueueMutation: EnqueueMutation }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: r.id })
  const style = { transform: CSS.Transform.toString(transform), transition }
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(r.title)
  const savingRef = useRef(false)
  const currentResponsibilityIdRef = useRef(r.id)
  currentResponsibilityIdRef.current = r.id
  useEffect(() => { savingRef.current = false }, [r.id])

  // An undo on the canvas puts the saved text back, so the editor has to open
  // on the text that is stored now rather than the one it was first given.
  // Only while it is closed: what is being typed is never overwritten.
  useEffect(() => {
    if (editing) return
    setTitle(r.title)
  }, [r.id, r.title, editing])

  const save = () => {
    if (!canEdit) { setEditing(false); return }
    if (savingRef.current) return
    if (title.trim() && title !== r.title) {
      savingRef.current = true
      const before = r.title
      const after = title
      enqueueMutation({
        key: `responsibility:${r.id}:update`,
        url: `${basePath}/responsibilities/${r.id}`,
        method: 'patch',
        data: { responsibility: { title } },
        onSuccess: () => {
          // Accepted by the server, so it can be taken back even if this row
          // has been replaced by another one in the meantime.
          onFieldEdit?.({
            label: `the ${LABELS.responsibility.toLowerCase()} "${before}"`,
            target: { type: 'responsibility', seatId, responsibilityId: r.id },
            before,
            after,
          })
        },
        onFinish: () => { if (currentResponsibilityIdRef.current === r.id) savingRef.current = false },
      }, `Unable to update this ${LABELS.responsibility.toLowerCase()}.`)
    }
    setEditing(false)
  }

  return (
    <li ref={setNodeRef} style={style} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-stone/20">
      {canEdit && <span {...attributes} {...listeners} className="cursor-grab text-text-muted"><GripVertical size={14} /></span>}
      {editing && canEdit ? (
        <input
          autoFocus value={title} onChange={e => setTitle(e.target.value)} onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setTitle(r.title); setEditing(false) } }}
          className="flex-1 text-sm border-b border-charcoal outline-none"
        />
      ) : (
        <span
          className={`flex-1 text-sm text-text-primary ${canEdit ? 'cursor-text' : ''}`}
          onClick={() => canEdit && setEditing(true)}
        >
          {r.title}
        </span>
      )}
      {canEdit && <button onClick={() => onDelete(r.id)} className="p-1 text-text-muted hover:text-pinnacle-red"><Trash2 size={13} /></button>}
    </li>
  )
}

function ResponsibilitiesSection({ basePath, seat, canEdit, onError, onFieldEdit, enqueueMutation }: { basePath: string; seat: SeatDetail; canEdit: boolean; onError: ReportError; onFieldEdit?: (edit: ChartFieldEdit) => void; enqueueMutation: EnqueueMutation }) {
  const [items, setItems] = useState(seat.responsibilities)
  const [newTitle, setNewTitle] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const currentSeatIdRef = useRef(seat.id)
  currentSeatIdRef.current = seat.id

  useEffect(() => { setItems(seat.responsibilities) }, [seat.id, seat.responsibilities])
  useEffect(() => {
    submittingRef.current = false
    setSubmitting(false)
  }, [seat.id])

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }))

  const onDragEnd = (e: DragEndEvent) => {
    if (!canEdit) return
    const { active, over } = e
    if (!over || active.id === over.id) return
    if (submittingRef.current) return
    const oldIdx = items.findIndex(i => i.id === active.id)
    const newIdx = items.findIndex(i => i.id === over.id)
    const reordered = arrayMove(items, oldIdx, newIdx)
    setItems(reordered)
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:responsibilities:reorder`,
      url: `${basePath}/responsibilities/reorder`,
      method: 'put',
      data: { responsibility_ids: reordered.map(r => r.id) },
      onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, `Unable to reorder ${LABELS.responsibilities.toLowerCase()}.`)
  }

  const add = () => {
    if (!canEdit) return
    const trimmedTitle = newTitle.trim()
    if (!trimmedTitle || items.length >= 15 || submittingRef.current) return
    if (items.some(item => item.title.trim().toLowerCase() === trimmedTitle.toLowerCase())) {
      onError({ base: `This ${LABELS.responsibility.toLowerCase()} is already listed.` })
      return
    }
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:responsibilities:add`,
      url: `${basePath}/responsibilities`,
      method: 'post',
      data: { responsibility: { title: trimmedTitle } },
      onSuccess: () => { if (currentSeatIdRef.current === seat.id) setNewTitle('') },
      onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, `Unable to add this ${LABELS.responsibility.toLowerCase()}.`)
  }
  const remove = (id: number) => {
    if (!canEdit) return
    if (!confirm(`Remove this ${LABELS.responsibility.toLowerCase()}?`)) return
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:responsibilities:remove:${id}`,
      url: `${basePath}/responsibilities/${id}`,
      method: 'delete',
      onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, `Unable to remove this ${LABELS.responsibility.toLowerCase()}.`)
  }

  const atCap = items.length >= 15
  const softWarn = items.length > 5

  return (
    <section>
      <h4 className="text-xs uppercase tracking-wider font-bold text-text-primary mb-2">{LABELS.responsibilities}</h4>
      {softWarn && !atCap && (
        <div className="mb-2 px-2 py-1.5 bg-marigold/10 text-marigold text-xs rounded">Pinnacle suggests 5 or fewer {LABELS.responsibilities.toLowerCase()} per {LABELS.seat.toLowerCase()}.</div>
      )}
      {atCap && (
        <div className="mb-2 px-2 py-1.5 bg-pinnacle-red/10 text-pinnacle-red text-xs rounded">Maximum of 15 {LABELS.responsibilities.toLowerCase()} reached.</div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map(r => r.id)} strategy={verticalListSortingStrategy}>
           <ul>{items.map(r => <SortableResponsibility key={r.id} r={r} seatId={seat.id} canEdit={canEdit} basePath={basePath} onDelete={remove} onFieldEdit={onFieldEdit} enqueueMutation={enqueueMutation} />)}</ul>
        </SortableContext>
      </DndContext>
      {canEdit && !atCap && (
        <div className="flex gap-1 mt-2">
           <input value={newTitle} onChange={e => setNewTitle(e.target.value)} disabled={submitting}
            onKeyDown={(e) => { if (e.key === 'Enter') add() }}
            placeholder={`Add ${indefiniteArticle(LABELS.responsibility)} ${LABELS.responsibility.toLowerCase()}…`}
            className="flex-1 px-2 py-1 border border-stone rounded-button text-sm" />
           <button onClick={add} disabled={submitting} className="px-2 py-1 text-sm text-text-primary hover:underline disabled:opacity-50">Add</button>
        </div>
      )}
    </section>
  )
}

function TagsSection({ basePath, seat, orgTags, canEdit, enqueueMutation }: { basePath: string; seat: SeatDetail; orgTags: SeatTag[]; canEdit: boolean; enqueueMutation: EnqueueMutation }) {
  const [showAdder, setShowAdder] = useState(false)
  const [newName, setNewName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const currentSeatIdRef = useRef(seat.id)
  currentSeatIdRef.current = seat.id
  useEffect(() => {
    submittingRef.current = false
    setSubmitting(false)
  }, [seat.id])

  const addTag = (tagId: number) => {
    if (!canEdit || submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:tag:add:${tagId}`,
      url: `${basePath}/seat_tags`,
      method: 'post',
      data: { tag_id: tagId },
       onSuccess: () => { if (currentSeatIdRef.current === seat.id) setShowAdder(false) },
       onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, 'Unable to add this tag.')
  }
  const createTag = () => {
    if (!canEdit || !newName.trim() || submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:tag:create`,
      url: `${basePath}/seat_tags`,
      method: 'post',
      data: { name: newName.trim() },
       onSuccess: () => { if (currentSeatIdRef.current === seat.id) { setNewName(''); setShowAdder(false) } },
       onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, 'Unable to create this tag.')
  }
  const remove = (seatTagId: number) => {
    if (!canEdit || submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:tag:remove:${seatTagId}`,
      url: `${basePath}/seat_tags/${seatTagId}`,
      method: 'delete',
       onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, 'Unable to remove this tag.')
  }

  const usedIds = new Set(seat.tags.map(t => t.id))
  const available = orgTags.filter(t => !usedIds.has(t.id))

  return (
    <section>
      <h4 className="text-xs uppercase tracking-wider font-bold text-text-primary mb-2">Tags</h4>
      <div className="flex flex-wrap gap-1.5">
        {seat.tags.map((t, idx) => {
          const seatTag = (seat as any).seat_tags?.[idx]?.id || t.id
          return (
            <span key={t.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone text-text-primary text-xs rounded-badge">
              {t.name}
               {canEdit && <button onClick={() => remove(seatTag)} disabled={submitting} className="text-text-muted hover:text-pinnacle-red disabled:opacity-50"><X size={10} /></button>}
            </span>
          )
        })}
        {seat.tags.length === 0 && <span className="text-xs text-text-muted italic">No tags</span>}
      </div>
      {canEdit && (
        <div className="mt-2">
          {!showAdder ? (
             <button onClick={() => setShowAdder(true)} disabled={submitting} className="text-sm text-text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50"><Plus size={14} />Add tag</button>
          ) : (
            <div className="border border-stone rounded-button p-2 space-y-1">
              {available.length > 0 && (
                <div className="max-h-32 overflow-y-auto">
                  {available.map(t => (
                     <button key={t.id} onClick={() => addTag(t.id)} disabled={submitting} className="w-full text-left text-sm px-2 py-1 hover:bg-stone/30 rounded disabled:opacity-50">{t.name}</button>
                  ))}
                </div>
              )}
              <div className="flex gap-1">
                 <input value={newName} onChange={e => setNewName(e.target.value)} disabled={submitting} placeholder="New tag name…"
                  onKeyDown={(e) => { if (e.key === 'Enter') createTag() }}
                  className="flex-1 px-2 py-1 border border-stone rounded text-xs" />
                 <button onClick={createTag} disabled={submitting} className="text-xs px-2 py-1 bg-pinnacle-red text-white rounded disabled:opacity-50">Create</button>
                 <button onClick={() => setShowAdder(false)} disabled={submitting} className="text-xs px-2 py-1 text-text-secondary disabled:opacity-50">Close</button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function CommentsSection({ basePath, seat, canEdit, enqueueMutation }: { basePath: string; seat: SeatDetail; canEdit: boolean; enqueueMutation: EnqueueMutation }) {
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const currentSeatIdRef = useRef(seat.id)
  currentSeatIdRef.current = seat.id
  useEffect(() => {
    submittingRef.current = false
    setSubmitting(false)
  }, [seat.id])

  const add = () => {
    if (!canEdit || !body.trim() || submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:comment:add`,
      url: `${basePath}/seat_comments`,
      method: 'post',
      data: { seat_comment: { body: body.trim() } },
       onSuccess: () => { if (currentSeatIdRef.current === seat.id) setBody('') },
       onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, 'Unable to post this comment.')
  }
  const remove = (id: number) => {
    if (!canEdit) return
    if (!confirm('Delete this comment?')) return
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    enqueueMutation({
      key: `seat:${seat.id}:comment:remove:${id}`,
      url: `${basePath}/seat_comments/${id}`,
      method: 'delete',
       onFinish: () => { if (currentSeatIdRef.current === seat.id) { submittingRef.current = false; setSubmitting(false) } },
    }, 'Unable to delete this comment.')
  }

  return (
    <section>
      <h4 className="text-xs uppercase tracking-wider font-bold text-text-primary mb-2">Comments</h4>
      <ul className="space-y-2 mb-2">
        {seat.comments.map(c => (
          <li key={c.id} className="bg-stone/30 rounded p-2 text-sm">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-text-primary text-xs">{c.author_name}</span>
              <span className="text-[10px] text-text-muted">{new Date(c.created_at).toLocaleDateString()}</span>
            </div>
            <p className="text-text-primary whitespace-pre-wrap">{c.body}</p>
            {canEdit && c.can_delete && (
               <button onClick={() => remove(c.id)} disabled={submitting} className="text-xs text-pinnacle-red hover:underline mt-1 disabled:opacity-50">Delete</button>
            )}
          </li>
        ))}
        {seat.comments.length === 0 && <li className="text-sm text-text-muted italic">No comments yet.</li>}
      </ul>
      {canEdit && (
        <>
          <textarea
             value={body}
            onChange={e => setBody(e.target.value)}
             disabled={submitting}
            rows={2}
            placeholder="Add a comment…"
            className="w-full px-2 py-1.5 border border-stone rounded-button text-sm focus:outline-none focus:border-charcoal"
          />
          <div className="flex justify-end mt-1">
             <button onClick={add} disabled={!body.trim() || submitting} className="text-xs px-3 py-1 bg-pinnacle-red text-white rounded disabled:opacity-50">Post</button>
          </div>
        </>
      )}
    </section>
  )
}
